import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import superAdminApi from "../lib/superAdminApi";

export interface SuperAdminUser {
  id: string;
  email: string;
  username: string;
  firstname?: string | null;
  lastname?: string | null;
  is_active: boolean;
  created_at?: string | null;
}

interface SuperAdminAuthState {
  superAdmin: SuperAdminUser | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: SuperAdminAuthState = {
  superAdmin: null,
  status: "idle",
  error: null,
};

export const loginSuperAdmin = createAsyncThunk(
  "superAdminAuth/login",
  async (credentials: { identifier: string; password: string }, { rejectWithValue }) => {
    try {
      const res = await superAdminApi.post("/superadmin/auth/login", credentials);
      if (res.data.fallbackToken) {
        localStorage.setItem("sa_fallbackToken", res.data.fallbackToken);
      }
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? "Login failed");
    }
  }
);

export const logoutSuperAdmin = createAsyncThunk(
  "superAdminAuth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await superAdminApi.post("/superadmin/auth/logout");
      localStorage.removeItem("sa_fallbackToken");
    } catch (err: any) {
      localStorage.removeItem("sa_fallbackToken");
      return rejectWithValue(err.response?.data?.message ?? "Logout error");
    }
  }
);

export const fetchSuperAdminMe = createAsyncThunk(
  "superAdminAuth/me",
  async (_, { rejectWithValue }) => {
    try {
      const res = await superAdminApi.get("/superadmin/auth/me");
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? "Failed to fetch profile");
    }
  }
);

const superAdminAuthSlice = createSlice({
  name: "superAdminAuth",
  initialState,
  reducers: {
    resetSuperAdminAuth: (state) => {
      state.superAdmin = null;
      state.status = "idle";
      state.error = null;
      localStorage.removeItem("sa_fallbackToken");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginSuperAdmin.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginSuperAdmin.fulfilled, (state, action: PayloadAction<any>) => {
        state.status = "succeeded";
        state.superAdmin = action.payload.superAdmin;
      })
      .addCase(loginSuperAdmin.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(logoutSuperAdmin.fulfilled, (state) => {
        state.superAdmin = null;
        state.status = "idle";
      })
      .addCase(fetchSuperAdminMe.fulfilled, (state, action: PayloadAction<any>) => {
        state.superAdmin = action.payload.superAdmin;
        state.status = "succeeded";
      })
      .addCase(fetchSuperAdminMe.rejected, (state) => {
        state.superAdmin = null;
        state.status = "idle";
      });
  },
});

export const { resetSuperAdminAuth } = superAdminAuthSlice.actions;
export const superAdminAuthReducer = superAdminAuthSlice.reducer;
